import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    return {
      user: null,
      token: null,
      loading: false,
      isAuthenticated: false,
      login: () => Promise.reject('Auth not initialized'),
      register: () => Promise.reject('Auth not initialized'),
      logout: () => {},
      updateProfile: () => Promise.reject('Auth not initialized'),
      forgotPassword: () => Promise.reject('Auth not initialized'),
      resetPassword: () => Promise.reject('Auth not initialized')
    };
  }
  return context;
};

// Use XMLHttpRequest to avoid fetch interception issues
const apiRequest = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const timeout = options.timeout || 30000; // 30 second timeout for mobile
    
    xhr.open(options.method || 'GET', url, true);
    xhr.timeout = timeout;
    
    // Set headers
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        xhr.setRequestHeader(key, options.headers[key]);
      });
    }
    
    xhr.onload = () => {
      let data = {};
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch (e) {
        console.error('Failed to parse response:', xhr.responseText);
      }
      resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data });
    };
    
    xhr.onerror = () => {
      console.error('XHR Error - readyState:', xhr.readyState, 'status:', xhr.status);
      reject(new Error('Unable to connect to server. Please check your internet connection and try again.'));
    };
    
    xhr.ontimeout = () => {
      console.error('XHR Timeout');
      reject(new Error('Request timed out. Please check your internet connection and try again.'));
    };
    
    xhr.send(options.body || null);
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('token');
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  // Use empty string for proxy through Netlify, or full URL for local dev
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const checkAuth = useCallback(async () => {
    let savedToken = null;
    try {
      savedToken = localStorage.getItem('token');
    } catch {
      // localStorage not available
    }
    
    // Always try to check auth if we have a token (proxy handles routing)
    if (savedToken) {
      try {
        const { ok, data } = await apiRequest(`${BACKEND_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${savedToken}`
          }
        });
        if (ok) {
          setUser(data);
          setToken(savedToken);
        } else {
          try { localStorage.removeItem('token'); } catch {}
          setToken(null);
          setUser(null);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        try { localStorage.removeItem('token'); } catch {}
        setToken(null);
        setUser(null);
      }
    }
    setLoading(false);
  }, [BACKEND_URL]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { ok, data } = await apiRequest(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!ok) {
      throw new Error(data.detail || data.message || 'Invalid email or password');
    }

    try { localStorage.setItem('token', data.access_token); } catch {}
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const register = async (userData) => {
    const { ok, data } = await apiRequest(`${BACKEND_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });

    if (!ok) {
      throw new Error(data.detail || data.message || 'Registration failed');
    }

    try { localStorage.setItem('token', data.access_token); } catch {}
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    try { localStorage.removeItem('token'); } catch {}
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (updateData) => {
    const { ok, data } = await apiRequest(`${BACKEND_URL}/api/auth/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateData)
    });

    if (!ok) {
      throw new Error(data.detail || data.message || 'Update failed');
    }

    setUser(data);
    return data;
  };

  const forgotPassword = async (email) => {
    const { ok, data } = await apiRequest(
      `${BACKEND_URL}/api/auth/forgot-password?email=${encodeURIComponent(email)}`,
      { method: 'POST' }
    );
    
    if (!ok) {
      throw new Error(data.detail || data.message || 'Failed to send reset email');
    }
    
    return data;
  };

  const resetPassword = async (resetToken, newPassword) => {
    const { ok, data } = await apiRequest(
      `${BACKEND_URL}/api/auth/reset-password?token=${encodeURIComponent(resetToken)}&new_password=${encodeURIComponent(newPassword)}`,
      { method: 'POST' }
    );

    if (!ok) {
      throw new Error(data.detail || data.message || 'Reset failed');
    }

    return data;
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateProfile,
    forgotPassword,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
